// Small DOM helpers.
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, unknown> = {},
  ...children: (Node | string | null | undefined | false)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') el.className = String(v);
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v as EventListener);
    else if (k === 'html') el.innerHTML = String(v);
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) el.append(c);
  return el;
}

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;

/** Escapes text, then renders **bold** and `code` from explanation strings. */
export function richText(s: string): string {
  const esc = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/`([^`]+)`/g, '<code>$1</code>');
}

/** A canvas that tracks its CSS size and device pixel ratio. */
export class Canvas {
  readonly el: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  w = 0;
  h = 0;
  dpr = 1;
  constructor(cls: string, readonly cssHeight: number) {
    this.el = h('canvas', { class: cls });
    this.el.style.height = cssHeight + 'px';
    this.ctx = this.el.getContext('2d')!;
  }
  /** Resizes the backing store; returns true if the size changed. */
  fit(width: number): boolean {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (width === this.w && dpr === this.dpr && this.el.width === Math.round(width * dpr)) return false;
    this.w = width;
    this.h = this.cssHeight;
    this.dpr = dpr;
    this.el.width = Math.round(width * dpr);
    this.el.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return true;
  }
  clear() {
    this.ctx.clearRect(0, 0, this.w, this.h);
  }
}

let toastTimer = 0;
export function toast(msg: string) {
  document.querySelector('.toast')?.remove();
  const t = h('div', { class: 'toast' }, msg);
  document.body.append(t);
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.remove(), 2200);
}

/** Shared hover tooltip. */
const tipEl = () => document.getElementById('tooltip')!;
export function showTip(html: string, x: number, y: number) {
  const t = tipEl();
  t.innerHTML = html;
  t.classList.add('on');
  const r = t.getBoundingClientRect();
  let left = x + 14, top = y + 16;
  if (left + r.width > window.innerWidth - 8) left = x - r.width - 14;
  if (top + r.height > window.innerHeight - 8) top = y - r.height - 12;
  t.style.left = Math.max(8, left) + 'px';
  t.style.top = Math.max(8, top) + 'px';
}
export function hideTip() {
  tipEl().classList.remove('on');
}
/** Attaches a hover tooltip to an element. */
export function tip(el: HTMLElement, html: string | (() => string)) {
  el.addEventListener('pointerenter', (e) => showTip(typeof html === 'function' ? html() : html, e.clientX, e.clientY));
  el.addEventListener('pointermove', (e) => showTip(typeof html === 'function' ? html() : html, e.clientX, e.clientY));
  el.addEventListener('pointerleave', hideTip);
}

export function menu(anchor: HTMLElement, items: { label: string; sub?: string; onClick: () => void }[]) {
  document.querySelector('.menu')?.remove();
  const m = h('div', { class: 'menu', role: 'menu' });
  for (const it of items)
    m.append(h('button', { role: 'menuitem', onclick: () => { m.remove(); it.onClick(); } }, it.label, it.sub ? h('span', { class: 'sub' }, it.sub) : null));
  document.body.append(m);
  const r = anchor.getBoundingClientRect();
  m.style.top = r.bottom + 6 + 'px';
  m.style.left = Math.max(8, Math.min(window.innerWidth - m.offsetWidth - 8, r.right - m.offsetWidth)) + 'px';
  const close = (e: Event) => {
    if (!m.contains(e.target as Node)) { m.remove(); document.removeEventListener('pointerdown', close, true); }
  };
  setTimeout(() => document.addEventListener('pointerdown', close, true));
  (m.querySelector('button') as HTMLButtonElement)?.focus();
}
